import { useEffect, useState } from 'react';
import { notificationsAPI } from '../../lib/api';
import { formatDate, cn } from '../../lib/utils';
import { X, Bell, AlertTriangle, FileText, Calendar, Scale, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';

const iconMap = {
  probation: Calendar,
  document_expiry: FileText,
  absence: Clock,
  disciplinary: Scale,
  contract: AlertTriangle,
};

export function NotificationsPanel({ onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="fixed right-0 top-0 h-screen w-96 bg-card border-l border-border shadow-xl z-50 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">Notifications</h2>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-notifications">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Notifications list */}
      <ScrollArea className="h-[calc(100vh-65px)]">
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const Icon = iconMap[notification.notification_type] || Bell;
              return (
                <div
                  key={notification.id}
                  data-testid={`notification-${notification.id}`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                  className={cn(
                    "p-4 rounded-lg border cursor-pointer transition-all",
                    notification.read 
                      ? "bg-card/50 border-border/50" 
                      : "bg-primary/5 border-primary/20 hover:bg-primary/10"
                  )}
                >
                  <div className="flex gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                      notification.read ? "bg-muted" : "bg-primary/20"
                    )}>
                      <Icon className={cn(
                        "w-5 h-5",
                        notification.read ? "text-muted-foreground" : "text-primary"
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={cn(
                        "font-medium text-sm",
                        !notification.read && "text-foreground"
                      )}>
                        {notification.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-2">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
